<template>
  <div>
    <input
      type="file"
      style="display: none"
      ref="selectedDocuments"
      multiple
      @change="onDocumentsPicked"
    />

    <!-- ActivityLog Dialog -->
    <v-dialog
      v-model="activityLogDialogOpened"
      width="800"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="activityLogDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title">
          {{ $t('documents.documentHistoryFor') }} {{ currentCompany.name }}
          <v-spacer />
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0"
            @click="activityLogDialogOpened = false"
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <activity-log
            :dialog-opened="activityLogDialogOpened"
            :current-company-id="currentCompanyId"
            :type-id="2"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Move Documents Dialog -->
    <v-dialog
      width="600"
      v-model="moveItemDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      @keydown.esc="moveItemDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="title">
          {{ $t('documents.specifyANewLocation') }}
        </v-card-title>
        <v-card-text>
          <v-layout align-center class="mb-2 subheading">
            <div class="mr-1">{{ deleteOldPath ? 'Move' : 'Copy' }} to:</div>
            <div :key="i" v-for="(path, i) in pathsInOrder">
              {{ i !== 0 ? '/' : ''
              }}<span
                class="primary--text font-weight-bold"
                @click="goBackPathForMove(path, i)"
                :style="i !== pathsInOrder.length - 1 ? 'cursor: pointer' : ''"
                >{{ isEmpty(path) ? 'Home' : path }}</span
              >
            </div>
            <v-spacer />
            <v-switch
              class="ma-0 pa-0 default-input"
              hide-details
              v-model="newEntityLocation"
              v-if="!isEmpty(otherUserCompanies)"
              :label="$t('documents.differentEntity')"
            />
          </v-layout>
          <v-layout align-center>
            <v-select
              v-if="newEntityLocation"
              class="default-input"
              solo
              item-value="id"
              item-text="name"
              :items="otherUserCompanies"
              :placeholder="$t('documents.selectNewEntity')"
              v-model="newCompanyId"
              @change="resetPath()"
            />
          </v-layout>
          <v-layout align-center v-if="!newEntityLocation || newCompanyId">
            <v-select
              class="default-input"
              solo
              item-value="value"
              item-text="label"
              :placeholder="
                isEmpty(itemsInLastPath)
                  ? $t('noFolder')
                  : $t('documents.chooseFolder')
              "
              :items="itemsInLastPath"
              :disabled="isEmpty(itemsInLastPath) || loading"
              v-model="lastPathInOrder"
              @change="changePath()"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="moveItem()"
            :disabled="
              pathsInOrder.join('/') === currentPath && !newEntityLocation
            "
          >
            {{ deleteOldPath ? $t('documents.move') : $t('documents.copy') }}
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="moveItemDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Move Multiple Documents Dialog -->
    <v-dialog
      width="600"
      v-model="moveMultipleDocumentsDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      persistent
      @keydown.esc="moveMultipleDocumentsDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="title">
          {{ $t('documents.specifyANewLocation') }}
        </v-card-title>
        <v-card-text>
          <v-layout align-center class="mb-2 subheading">
            <div class="mr-1">
              {{
                deleteOldPath ? $t('documents.moveTo') : $t('documents.copyTo')
              }}:
            </div>
            <div :key="i" v-for="(path, i) in pathsInOrder">
              {{ i !== 0 ? '/' : ''
              }}<span
                class="primary--text font-weight-bold"
                @click="goBackPathForMove(path, i)"
                :style="i !== pathsInOrder.length - 1 ? 'cursor: pointer' : ''"
                >{{ isEmpty(path) ? 'Home' : path }}</span
              >
            </div>
            <v-spacer />
            <v-switch
              class="ma-0 pa-0 default-input"
              hide-details
              v-model="newEntityLocation"
              v-if="!isEmpty(otherUserCompanies)"
              :label="$t('differentEntity')"
            />
          </v-layout>
          <v-layout align-center>
            <v-select
              v-if="newEntityLocation"
              class="default-input"
              solo
              item-value="id"
              item-text="name"
              :items="otherUserCompanies"
              :placeholder="$t('documents.selectNewEntity')"
              v-model="newCompanyId"
              @change="resetPath()"
            />
          </v-layout>
          <v-layout align-center v-if="!newEntityLocation || newCompanyId">
            <v-select
              class="default-input"
              solo
              item-value="value"
              item-text="label"
              :placeholder="
                isEmpty(itemsInLastPath)
                  ? $t('noFolder')
                  : $t('documents.chooseFolder')
              "
              :items="itemsInLastPath"
              :disabled="isEmpty(itemsInLastPath)"
              v-model="lastPathInOrder"
              @change="changePath()"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="moveMultipleDocuments()"
            :disabled="pathsInOrder.join('/') === currentPath"
          >
            {{ deleteOldPath ? $t('documents.move') : $t('documents.copy') }}
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="moveMultipleDocumentsDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <ConfirmationDialog
      v-model="deleteConfirmationDialogOpened"
      @confirm="deleteDocument(documentToDelete)"
    >
      {{ $t('documents.deletionConfirmation') }}
      <b>{{ documentToDelete.name }}</b
      >?
    </ConfirmationDialog>

    <!-- Delete Multiple Confirmation Dialog -->
    <ConfirmationDialog
      v-model="deleteMultipleConfirmationDialogOpened"
      @confirm="deleteMultipleDocuments()"
    >
      {{ $t('documents.deletionConfirmation') }} {{ selectedDocuments.length }}
      {{ $t('documents.items') }}?
    </ConfirmationDialog>

    <!-- Download Multiple Documents Dialog -->
    <ConfirmationDialog
      v-model="downloadMultipleConfirmationDialogOpened"
      @confirm="downloadMultipleDocuments()"
    >
      {{ $t('documents.downloadConfirmation') }}
      {{ selectedDocuments.filter(doc => doc.type === 'file').length }}
      {{ $t('documents.files') }}?
      {{ $t('documents.downloadMultipleDocumentsHelp') }}
    </ConfirmationDialog>

    <!-- Preview Dialog -->
    <v-dialog
      max-width="1000px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="previewDialogOpened"
      @keydown.esc="
        previewDialogOpened = false;
        documentPreviewData = '';
        previewRotation = 0;
      "
    >
      <v-card v-if="selectedItem && previewType">
        <v-card-title class="title">
          <v-layout v-if="renameFile" align-center>
            <span class="caption">{{ $t('documents.newName') }}:</span>
            <v-text-field
              v-model="newName"
              hide-details
              solo
              class="default-input mx-2"
            />
            <span class="subheading">.{{ documentExtension }}</span>
            <v-tooltip bottom class="mx-2">
              <i
                class="material-icons-outlined success--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameItem()"
                slot="activator"
              >
                check
              </i>
              <span>{{ $t('documents.save') }}</span>
            </v-tooltip>
            <v-tooltip bottom class="ml-2">
              <i
                class="material-icons-outlined error--text"
                style="font-size: 20px; cursor: pointer"
                @click="renameFile = false"
                slot="activator"
              >
                cancel
              </i>
              <span>{{ $t('cancel') }}</span>
            </v-tooltip>
          </v-layout>
          <v-layout v-else>
            <span class="text-truncate" style="max-width: 600px">{{
              selectedItem.name
            }}</span>
            <v-tooltip bottom class="ml-2">
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 20px; cursor: pointer"
                @click="startRenaming()"
                slot="activator"
              >
                edit
              </i>
              <span>{{ $t('documents.rename') }}</span>
            </v-tooltip>
          </v-layout>

          <v-spacer />
          <template v-if="$vuetify.breakpoint.mdAndUp">
            <v-tooltip bottom class="mr-1">
              <v-btn
                fab
                small
                flat
                class="white primary--text text-capitalize ma-0"
                @click="previewRotation += 90"
                slot="activator"
              >
                <v-icon size="25"> rotate_right </v-icon>
              </v-btn>
              <span>{{ $t('documents.rotate') }}</span>
            </v-tooltip>
            <v-tooltip bottom class="mx-1">
              <v-btn
                fab
                small
                flat
                class="white primary--text text-capitalize ma-0"
                @click="openMoveDialog(selectedItem)"
                slot="activator"
              >
                <i
                  class="material-icons-outlined primary--text"
                  style="font-size: 25px"
                >
                  drive_file_move
                </i>
              </v-btn>
              <span>{{ $t('documents.moveDocument') }}</span>
            </v-tooltip>
            <v-tooltip bottom class="mx-1">
              <v-btn
                fab
                small
                flat
                class="white primary--text text-capitalize ma-0"
                @click="openCopyDialog(selectedItem)"
                slot="activator"
              >
                <i
                  class="material-icons-outlined primary--text"
                  style="font-size: 25px"
                >
                  content_copy
                </i>
              </v-btn>
              <span>{{ $t('documents.copyDocument') }}</span>
            </v-tooltip>
          </template>

          <v-tooltip bottom class="mx-1">
            <v-btn
              fab
              small
              flat
              class="white primary--text text-capitalize ma-0"
              @click="downloadDocument(selectedItem)"
              slot="activator"
            >
              <i
                class="material-icons-outlined primary--text"
                style="font-size: 25px"
              >
                get_app
              </i>
            </v-btn>
            <span>{{ $t('documents.downloadToComputer') }}</span>
          </v-tooltip>
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0 ml-1"
            @click="
              previewDialogOpened = false;
              documentPreviewData = '';
              previewRotation = 0;
            "
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text v-if="documentPreviewData && previewLoaded">
          <preview-document
            :document-preview-data="documentPreviewData"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
          />
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
        <v-card-actions
          class="justify-center mobile-dialog-footer"
          v-if="$vuetify.breakpoint.smAndDown"
        >
          <v-btn
            fab
            small
            flat
            class="white primary--text text-capitalize my-0 mx-2"
            @click="previewRotation += 90"
            slot="activator"
          >
            <v-icon size="25"> rotate_right </v-icon>
          </v-btn>
          <v-btn
            fab
            small
            flat
            class="white primary--text text-capitalize ma-0 mx-2"
            @click="openMoveDialog(selectedItem)"
            slot="activator"
          >
            <i
              class="material-icons-outlined primary--text"
              style="font-size: 25px"
            >
              drive_file_move
            </i>
          </v-btn>
          <v-btn
            fab
            small
            flat
            class="white primary--text text-capitalize ma-0 mx-2"
            @click="openCopyDialog(selectedItem)"
            slot="activator"
          >
            <i
              class="material-icons-outlined primary--text"
              style="font-size: 25px"
            >
              content_copy
            </i>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Import Documents Dialog -->
    <v-dialog
      width="600"
      v-model="importDocumentsDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="importDocumentsDialogOpened = false"
    >
      <v-card flat @drop.prevent="addFile" @dragover.prevent>
        <v-card-title class="title pb-0">
          {{ $t('documents.uploadDocuments') }}
        </v-card-title>
        <v-card-text>
          <v-alert
            :value="true"
            v-if="
              !isEmpty(
                existingQuestionnaires.filter(a => !a.completed && !a.locked),
              )
            "
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'warning' : ''"
            color="error"
            class="text-xs-left mb-2"
          >
            {{ $t('documents.ongoingQuestionnaireWarning') }}<br />
            <span
              class="black--text font-weight-bold"
              style="cursor: pointer"
              @click="goToQuestionnaire(questionnaire.id)"
              :key="questionnaire.id"
              v-for="questionnaire in existingQuestionnaires.filter(
                a => !a.completed && !a.locked,
              )"
            >
              <br />{{ questionnaire.name }} ({{ $t('documents.clickToView') }})
            </span>
            <br /><br />
          </v-alert>
          <v-alert
            :value="true"
            v-else-if="
              !isEmpty(
                existingQuestionnaires.filter(a => a.completed && !a.locked),
              )
            "
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'warning' : ''"
            color="error"
            class="text-xs-left mb-2"
          >
            {{ $t('documents.completedQuestionnaireWarning') }}
            <span
              class="font-weight-bold"
              style="text-decoration: underline; cursor: pointer"
              v-if="!isFirmCommunicationDisabled"
              @click="openRelevantConversation"
              >({{ $t('documents.clickHere') }})</span
            ><br />
            <span
              class="black--text font-weight-bold"
              :key="questionnaire.id"
              v-for="questionnaire in existingQuestionnaires.filter(
                a => a.completed && !a.locked,
              )"
            >
              <br />{{ questionnaire.name }}
            </span>
            <br /><br />
          </v-alert>
          <v-alert
            :value="true"
            v-else-if="!isEmpty(existingQuestionnaires.filter(a => a.locked))"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'warning' : ''"
            color="error"
            class="text-xs-left mb-2"
          >
            {{ $t('documents.lockedQuestionnaireWarning') }}
            <span
              class="font-weight-bold"
              style="text-decoration: underline; cursor: pointer"
              v-if="!isFirmCommunicationDisabled"
              @click="openRelevantConversation"
              >({{ $t('documents.clickHere') }})</span
            ><br />
            <span
              class="black--text font-weight-bold"
              :key="questionnaire.id"
              v-for="questionnaire in existingQuestionnaires.filter(
                a => a.locked,
              )"
            >
              <br />{{ questionnaire.name }}
            </span>
            <br /><br />
          </v-alert>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mb-4"
          >
            {{ $t('alerts.dragAndDropFiles') }}
          </v-alert>
          <v-layout column>
            <v-flex class="text-xs-left mb-2 font-weight-bold">
              <v-layout align-center>
                <div class="py-3">{{ $t('documents.selectedDocuments') }}:</div>
                <v-spacer />
                <v-progress-circular
                  v-if="documentImporting"
                  indeterminate
                  size="25"
                  class="primary--text mr-2"
                />
                <v-btn
                  outline
                  class="default-input text-capitalize primary--text"
                  :disabled="documentImporting"
                  @click="pickDocuments"
                >
                  <v-layout align-center>
                    <v-icon class="mr-2" size="20">add</v-icon>
                    {{ $t('documents.selectFiles') }}
                  </v-layout>
                </v-btn>
              </v-layout>
            </v-flex>
            <v-flex class="text-xs-left mb-4" v-if="isEmpty(documentsToImport)">
              {{ $t('documents.noDocumentsSelected') }}
            </v-flex>
            <v-flex class="mb-4" v-else>
              <v-layout
                align-center
                :key="i"
                v-for="(doc, i) in documentsToImport"
              >
                <v-icon color="primary"> insert_drive_file </v-icon>
                <span class="ml-1 my-1">{{ doc.name }}</span>
                <v-spacer />
                <v-icon
                  @click="documentsToImport.splice(i, 1)"
                  style="cursor: pointer"
                  color="error"
                  >cancel</v-icon
                >
              </v-layout>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="importDocuments()"
            :disabled="documentImporting || isEmpty(documentsToImport)"
          >
            {{ $t('documents.uploadAll') }}
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="
              importDocumentsDialogOpened = false;
              documentsToImport = [];
            "
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create Folder Dialog -->
    <v-dialog
      width="600"
      v-model="createFolderDialogOpened"
      persistent
      @keydown.esc="createFolderDialogOpened = false"
    >
      <v-card flat>
        <v-card-title class="title pb-0">
          {{ $t('documents.createFolder') }}
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="isAdvisor"
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mb-4 mx-2"
          >
            Do not include slashes (/) or periods (.) and add
            <b>STAFF ONLY</b> in the folder name if you want the folder to be
            hidden from the client's view.
          </v-alert>
          <v-alert
            v-else
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="primary"
            class="text-xs-left mb-4 mx-2"
          >
            {{ $t('documents.folderCreationWarning') }}
          </v-alert>
          <v-layout class="text-xs-left" column>
            <v-flex>
              <v-flex class="mb-2">{{ $t('documents.folderName') }}</v-flex>
              <v-text-field
                v-model="newFolderName"
                solo
                class="default-input"
                :placeholder="$t('documents.enterNewFolderName')"
                hide-details
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="createFolder"
            :disabled="
              !newFolderName.length ||
                newFolderName.includes('/') ||
                newFolderName.includes('.')
            "
          >
            {{ $t('documents.create') }}
          </v-btn>
          <v-btn
            class="text-capitalize error--text"
            outline
            @click="createFolderDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div @drop.prevent="addFile" @dragover.prevent>
      <v-card flat class="content-container mt-4">
        <v-card-text>
          <v-flex>
            <v-alert
              :value="true"
              outline
              block
              type="info"
              class="text-xs-left"
              v-if="isAdvisor"
            >
              {{ $t('alerts.uploadFiles') }} Add <b>STAFF ONLY</b> in the name
              if you want to hide a file or folder from the client's view.
            </v-alert>
            <v-alert
              :value="true"
              outline
              block
              type="info"
              class="text-xs-left"
              v-else
            >
              {{ $t('alerts.uploadFiles') }}
            </v-alert>
          </v-flex>
          <v-layout class="mb-2" align-center>
            <v-layout align-center>
              <soraban-action-button
                v-if="currentFolder !== $t('documents.allDocuments')"
                @click="goBack"
                icon="keyboard_arrow_left"
                :help="$t('back')"
              />
              <v-flex class="ml-2 body-1 text-xs-left secondary--text"
                ><span class="hidden-sm-and-down"
                  >{{ $t('documents.currentFolder') }}: </span
                ><span class="accent--text font-weight-bold">{{
                  currentFolder
                }}</span></v-flex
              >
            </v-layout>
            <v-spacer />
            <soraban-action-button
              @click="activityLogDialogOpened = true"
              icon="schedule"
              help="History"
              v-if="isAdvisor"
            />
            <soraban-action-button
              v-if="
                !currentUser.readonly_access && !currentCompany.readonly_access
              "
              @click="openImportDocumentsDialog()"
              icon="add"
              :help="$t('documents.uploadDocuments')"
            />
            <soraban-action-button
              @click="createFolderDialogOpened = true"
              icon="create_new_folder"
              :help="$t('documents.createFolder')"
              v-if="
                !currentUser.readonly_access &&
                  !currentCompany.readonly_access &&
                  (!disableFolderAction || isAdvisor)
              "
            />
            <v-menu
              transition="slide-y-transition"
              bottom
              right
              class="my-0"
              v-if="!isEmpty(selectedDocuments)"
            >
              <soraban-action-button
                icon="more_vert"
                help="Actions for Selected"
                slot="activator"
              />
              <v-list class="caption">
                <v-list-tile @click="openMoveMultipleDocumentsDialog(true)">
                  <v-list-tile-title>
                    {{ $t('documents.moveSelected') }} ({{
                      selectedDocuments.length
                    }})
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile @click="openMoveMultipleDocumentsDialog(false)">
                  <v-list-tile-title>
                    {{ $t('documents.copySelected') }} ({{
                      selectedDocuments.length
                    }})
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  @click="deleteMultipleConfirmationDialogOpened = true"
                >
                  <v-list-tile-title>
                    {{ $t('documents.deleteSelected') }} ({{
                      selectedDocuments.length
                    }})
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  @click="downloadMultipleConfirmationDialogOpened = true"
                  v-if="
                    !isEmpty(
                      selectedDocuments.filter(doc => doc.type === 'file'),
                    )
                  "
                >
                  <v-list-tile-title>
                    {{ $t('documents.downloadSelected') }} ({{
                      selectedDocuments.filter(doc => doc.type === 'file')
                        .length
                    }})
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </v-layout>
          <v-layout class="subheading mt-3 mb-2" align-center justify-center>
            <v-spacer class="hidden-sm-and-down" />
            <v-flex class="hidden-sm-and-down">
              <v-text-field
                v-model="documentSearch"
                append-icon="search"
                single-line
                :placeholder="$t('documents.search')"
                hide-details
                solo
                class="default-input"
              />
            </v-flex>
          </v-layout>
          <v-layout column class="pb-4">
            <v-data-table
              :headers="headers"
              :items="documents"
              hide-actions
              disable-initial-sort
              :loading="loading"
              :search="documentSearch"
              class="documents-table"
              :select-all="$vuetify.breakpoint.mdAndUp"
              v-model="selectedDocuments"
              item-key="name"
            >
              <template slot="items" slot-scope="props">
                <tr :key="props.index" v-if="!hiddenItem(props.item)">
                  <td class="hidden-sm-and-down" style="max-width: 10px">
                    <v-checkbox
                      v-model="props.selected"
                      hide-details
                      color="lightgrey"
                    ></v-checkbox>
                  </td>

                  <td class="text-xs-left ph-no-capture">
                    <v-layout align-center>
                      <v-icon
                        class="primary--text text--lighten-1"
                        v-if="props.item.type === 'folder'"
                        >folder</v-icon
                      >
                      <v-icon
                        class="grey--text text--darken-2 material-icons-outlined"
                        v-else
                        >insert_drive_file</v-icon
                      >
                      <v-layout v-if="props.item.renameFile" align-center>
                        <v-text-field
                          v-model="newName"
                          hide-details
                          solo
                          class="default-input mx-2"
                        />
                        <span
                          v-if="props.item.type === 'file'"
                          class="black--text subheading"
                          >.{{ documentExtension }}</span
                        >
                        <v-tooltip bottom class="mx-1 pt-1">
                          <i
                            class="material-icons-outlined success--text"
                            style="font-size: 20px; cursor: pointer"
                            @click="renameItem()"
                            slot="activator"
                          >
                            check
                          </i>
                          <span>{{ $t('documents.save') }}</span>
                        </v-tooltip>
                        <v-tooltip bottom class="ml-1 pt-1">
                          <i
                            class="material-icons-outlined error--text"
                            style="font-size: 20px; cursor: pointer"
                            @click="props.item.renameFile = false"
                            slot="activator"
                          >
                            cancel
                          </i>
                          <span>{{ $t('cancel') }}</span>
                        </v-tooltip>
                      </v-layout>
                      <v-layout
                        v-else
                        class="ml-2"
                        style="cursor: pointer"
                        align-center
                        @click="
                          props.item.type === 'folder'
                            ? goToFolder(props.item.name)
                            : previewDocument(props.item)
                        "
                      >
                        <span class="ph-no-capture">{{ props.item.name }}</span>
                        <v-chip
                          class="mr-0"
                          small
                          v-if="props.item.name.includes('STAFF ONLY')"
                          >HIDDEN</v-chip
                        >
                      </v-layout>
                    </v-layout>
                  </td>

                  <td v-if="$vuetify.breakpoint.smAndUp">
                    <v-layout align-center v-if="!!props.item.updated_at">
                      <span class="documents-row__updated-date">{{
                        moment(props.item.updated_at).format('lll')
                      }}</span>
                    </v-layout>
                    <v-layout align-center v-else>
                      <span class="documents-row__updated-date">-</span>
                    </v-layout>
                  </td>

                  <td>
                    <v-layout
                      align-center
                      v-if="
                        !disableFolderAction ||
                          isAdvisor ||
                          props.item.type === 'file'
                      "
                    >
                      <v-menu
                        transition="slide-y-transition"
                        bottom
                        right
                        class="my-0"
                      >
                        <i
                          slot="activator"
                          class="material-icons-outlined darkgrey--text"
                          :disabled="renameFile || loading"
                        >
                          more_vert
                        </i>
                        <v-list class="caption">
                          <v-list-tile @click="startRenaming(props.item)">
                            <v-list-tile-title>
                              {{ $t('documents.rename') }}
                            </v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile @click="openMoveDialog(props.item)">
                            <v-list-tile-title>
                              {{ $t('documents.move') }}
                            </v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile @click="openCopyDialog(props.item)">
                            <v-list-tile-title>
                              {{ $t('documents.copy') }}
                            </v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile
                            @click="openDeleteConfirmationDialog(props.item)"
                          >
                            <v-list-tile-title>
                              {{ $t('documents.delete') }}
                            </v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile
                            @click="downloadFolder(props.item)"
                            v-if="
                              props.item.type === 'folder' &&
                                currentUser.is_advisor
                            "
                          >
                            <v-list-tile-title>
                              {{ $t('documents.download') }}
                            </v-list-tile-title>
                          </v-list-tile>
                          <v-list-tile
                            @click="downloadDocument(props.item)"
                            v-else-if="props.item.type !== 'folder'"
                          >
                            <v-list-tile-title>
                              {{ $t('documents.download') }}
                            </v-list-tile-title>
                          </v-list-tile>
                        </v-list>
                      </v-menu>
                    </v-layout>
                  </td>
                </tr>
              </template>
              <template slot="no-data">
                <v-layout class="text-xs-center grey--text">
                  <v-flex>
                    <v-icon size="16" color="grey"> priority_high </v-icon>
                    <span class="ml-1">{{ $t('documents.emptyFolder') }}</span>
                  </v-flex>
                </v-layout>
              </template>
            </v-data-table>
          </v-layout>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import has from 'lodash/has';
import PreviewDocument from '@/components/PreviewDocument';
import ActivityLog from '@/components/ActivityLog';
import { supportedExtensionsForPreview } from '@/constants/variables';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import UtilsMixins from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';
// import posthog from 'posthog-js';
import JSZip from 'jszip';
import download from 'downloadjs';

export default {
  components: {
    PreviewDocument,
    ActivityLog,
    ConfirmationDialog,
  },

  mixins: [UtilsMixins, FirmPreferenceMixins],

  props: {
    currentCompanyId: {
      type: Number,
    },

    currentCompany: {
      type: Object,
    },
  },

  data() {
    return {
      supportedExtensionsForPreview,
      documentPreviewData: '',
      pdfLoaded: false,
      previewType: '',
      previewDialogOpened: false,
      currentPage: 1,
      renameFile: false,
      pageCount: 1,
      deleteOldPath: false,
      createFolderDialogOpened: false,
      deleteConfirmationDialogOpened: false,
      deleteMultipleConfirmationDialogOpened: false,
      downloadMultipleConfirmationDialogOpened: false,
      importDocumentsDialogOpened: false,
      renameItemDialogOpened: false,
      selectedItem: {
        name: '',
        type: 'file',
      },
      lastPathInOrder: {
        name: '',
        type: 'file',
      },
      newName: '',
      documentToDelete: {
        name: '',
      },
      newEntityLocation: false,
      documentsToImport: [],
      selectedDocuments: [],
      documentName: '',
      previewRotation: 0,
      documentActualName: '',
      documentExtension: '',
      documentData: '',
      newFolderName: '',
      documentSearch: '',
      documentImporting: false,
      loading: false,
      previewLoaded: false,
      documents: [],
      currentPath: '',
      currentFolder: this.$t('documents.allDocuments'),
      documentHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '40%',
        },
        {
          text: 'Last Modified',
          align: 'left',
          sortable: true,
          value: 'updated_at',
          width: '40%',
        },
        {
          text: 'Actions',
          align: 'left',
          sortable: false,
          width: '20%',
        },
      ],
      mobileDocumentHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '50%',
        },
        {
          text: 'Actions',
          align: 'left',
          sortable: false,
          width: '5%',
        },
      ],
      files: [],
      dragAndDropFileDialogOpened: true,
      moveItemDialogOpened: false,
      moveMultipleDocumentsDialogOpened: false,
      pathsInOrder: [],
      itemsInLastPath: [],
      newCompanyId: null,
      activityLogDialogOpened: false,
      existingQuestionnaires: [],
    };
  },

  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      isAdvisor: 'isAdvisor',
      defaultCompany: 'defaultCompany',
    }),
    ...mapGetters([
      'currentFirm',
      'isFirmDocumentsDisabled',
      'isFirmCommunicationDisabled',
    ]),

    headers() {
      if (this.$vuetify.breakpoint.xsOnly) {
        return this.mobileDocumentHeaders;
      } else {
        return this.documentHeaders;
      }
    },

    paths() {
      const paths = this.currentPath.split('/');
      if (paths) {
        return paths.map((name, index) => {
          return {
            text: name,
            path: paths.slice(0, index + 1).join('/'),
            disabled: paths.length - 1 === index,
          };
        });
      } else {
        return [];
      }
    },
    uploadDisabled() {
      return this.files.length === 0;
    },
    documentUploadReady() {
      return !isEmpty(this.documentActualName) && !isEmpty(this.documentName);
    },
    displayName() {
      if (this.currentCompany.entity_type === 'Individual') {
        return this.currentCompany.tax_return_name;
      } else {
        return `${this.currentCompany.name} (${this.currentCompany.user.full_name})`;
      }
    },
    otherUserCompanies() {
      if (!this.currentCompany.user) {
        return [];
      }

      if (this.currentCompany.user.is_advisor) {
        return this.companies.filter(c => {
          return (
            c.user.id === this.currentCompany.user.id &&
            c.id !== this.currentCompanyId
          );
        });
      } else {
        return this.companies.filter(c => {
          return (
            this.currentCompany.user.owned_company_ids.includes(c.id) &&
            c.id !== this.currentCompanyId
          );
        });
      }
    },
  },

  methods: {
    moment,
    get,
    isEmpty,
    openRelevantConversation() {
      this.importDocumentsDialogOpened = false;
      this.$emit('open-relevant-conversation');
    },
    startRenaming(item) {
      if (item) {
        this.selectedItem = item;
        item.renameFile = true;
      } else {
        this.renameFile = true;
      }
      this.documentExtension = this.selectedItem.name.split('.').pop();
      this.newName = this.selectedItem.name.replace(
        `.${this.documentExtension}`,
        '',
      );
    },
    openMoveMultipleDocumentsDialog(deleteOldPath) {
      this.deleteOldPath = deleteOldPath;
      this.newCompanyId = this.currentCompanyId;
      this.newEntityLocation = false;
      this.pathsInOrder = this.currentPath.split('/');
      this.itemsInLastPath = this.documents
        .filter(d => d.type === 'folder' && d.name !== '_soraban')
        .map(d => d.name);
      this.lastPathInOrder = this.currentPath.split('/')[
        this.currentPath.split('/').length - 1
      ];
      this.moveMultipleDocumentsDialogOpened = true;
    },
    openMoveDialog(item) {
      this.selectedItem = item;
      this.deleteOldPath = true;
      this.newEntityLocation = false;
      this.newCompanyId = this.currentCompanyId;
      this.pathsInOrder = this.currentPath.split('/');
      this.itemsInLastPath = this.documents
        .filter(d => d.type === 'folder' && d.name !== '_soraban')
        .map(d => d.name);
      this.moveItemDialogOpened = true;
    },
    openCopyDialog(item) {
      this.selectedItem = item;
      this.deleteOldPath = false;
      this.newEntityLocation = false;
      this.newCompanyId = this.currentCompanyId;
      this.pathsInOrder = this.currentPath.split('/');
      this.itemsInLastPath = this.documents
        .filter(d => d.type === 'folder' && d.name !== '_soraban')
        .map(d => d.name);
      this.lastPathInOrder = this.currentPath.split('/')[
        this.currentPath.split('/').length - 1
      ];
      this.moveItemDialogOpened = true;
    },
    async goBackPathForMove(path, index) {
      if (index !== this.pathsInOrder.length - 1 && !this.loading) {
        this.loading = true;
        this.lastPathInOrder = null;
        this.pathsInOrder = this.pathsInOrder.slice(0, index + 1);
        const response = await API.integration.getDocuments({
          companyId: this.currentCompanyId,
          path: this.pathsInOrder.join('/'),
        });
        this.itemsInLastPath = response.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.loading = false;
      }
    },
    async changePath() {
      if (this.newCompanyId) {
        const response2 = await API.integration.getDocuments({
          companyId: this.newCompanyId,
          path: this.pathsInOrder.join('/').concat('/', this.lastPathInOrder),
        });
        this.itemsInLastPath = response2.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.pathsInOrder.push(this.lastPathInOrder);
        this.lastPathInOrder = null;
      } else {
        const response2 = await API.integration.getDocuments({
          companyId: this.currentCompanyId,
          path: this.pathsInOrder.join('/').concat('/', this.lastPathInOrder),
        });
        this.itemsInLastPath = response2.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
        this.pathsInOrder.push(this.lastPathInOrder);
        this.lastPathInOrder = null;
      }
    },
    async resetPath() {
      this.itemsInLastPath = [];
      this.pathsInOrder = [''];
      this.lastPathInOrder = null;
      if (this.newCompanyId) {
        const response = await API.integration.getDocuments({
          companyId: this.newCompanyId,
        });
        this.itemsInLastPath = response.data.documents
          .filter(d => d.type === 'folder' && d.name !== '_soraban')
          .map(d => d.name);
      }
    },
    async moveItem() {
      try {
        this.moveItemDialogOpened = false;
        this.loading = true;
        await API.integration.moveItem({
          oldPath: this.currentPath.concat('/', this.selectedItem.name),
          newPath: this.pathsInOrder
            .join('/')
            .concat('/', this.selectedItem.name),
          deleteOldPath: this.deleteOldPath,
          companyId: this.currentCompanyId,
          newCompanyId:
            this.newCompanyId === this.currentCompanyId
              ? null
              : this.newCompanyId,
          isFolder: this.selectedItem.type === 'folder',
        });
        await this.getDocuments(this.currentPath);
        this.$store.dispatch('application/setSuccessNotification', {
          message: this.this.deleteOldPath
            ? this.$t('documents.itemMoveSuccessMessage')
            : this.$t('documents.itemCopySuccessMessage'),
        });
        this.previewDialogOpened = false;
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: this.this.deleteOldPath
            ? this.$t('documents.itemMoveErrorMessage')
            : this.$t('documents.itemCopyErrorMessage'),
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
        this.pathsInOrder = [];
      }
    },
    async moveMultipleDocuments() {
      try {
        this.loading = true;
        this.moveMultipleDocumentsDialogOpened = false;

        await this.asyncForEach(this.selectedDocuments, async doc => {
          if (
            doc.name !== '_soraban' &&
            (!this.disableFolderAction || this.isAdvisor || doc.type === 'file')
          ) {
            await API.integration.moveItem({
              oldPath: this.currentPath.concat('/', doc.name),
              newPath: this.pathsInOrder.join('/').concat('/', doc.name),
              deleteOldPath: this.deleteOldPath,
              companyId: this.currentCompanyId,
              newCompanyId:
                this.newCompanyId === this.currentCompanyId
                  ? null
                  : this.newCompanyId,
              isFolder: doc.type === 'folder',
            });
          }
        });
        this.$store.dispatch('application/setSuccessNotification', {
          message: this.this.deleteOldPath
            ? this.$t('documents.bulkItemsMoveSuccessMessage')
            : this.$t('documents.bulkItemsCopySuccessMessage'),
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: this.this.deleteOldPath
            ? this.$t('documents.bulkItemsMoveErrorMessage')
            : this.$t('documents.bulkItemsCopyErrorMessage'),
          errorToSend: response.data.error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
      }
    },
    hiddenItem(item) {
      if (this.isAdvisor) {
        return item.type === 'folder' && item.name === '_soraban';
      } else {
        return (
          (item.type === 'folder' && item.name === '_soraban') ||
          item.name.includes('STAFF ONLY')
        );
      }
    },
    async openImportDocumentsDialog() {
      this.documentsToImport = [];
      this.documentImporting = false;
      this.importDocumentsDialogOpened = true;
    },
    async importDocuments() {
      try {
        this.loading = true;
        this.importDocumentsDialogOpened = false;
        const path = this.currentPath;
        const companyId = this.currentCompanyId;
        let success = 0;

        for (const documentToImport of this.documentsToImport) {
          const trueExtension = await this.getTrueExtension(documentToImport);
          const currentExtension = this.getExtensionOfFilename(
            documentToImport.name,
          );
          const extensionsToCheck = ['pdf', 'png', 'gif', 'jpeg'];

          if (documentToImport.size === 0) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be EMPTY. Kindly check those files and try uploading them again.`,
            );
          }

          if (
            extensionsToCheck.includes(currentExtension) &&
            trueExtension !== currentExtension
          ) {
            throw new Error(
              `We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.`,
            );
          }

          if (currentExtension === 'pdf') {
            if (await this.pdfHasPassword(documentToImport)) {
              throw new Error(
                `The PDFs you uploaded are password-protected. Please either remove the password or upload alternative files.`,
              );
            }
          }

          const {
            data: { id, upload_link },
          } = await API.integration.getTemporaryUploadLink({
            companyId: this.currentCompanyId,
          });

          await API.integration.uploadToDropbox(upload_link, documentToImport);

          const response = await API.integration.processDocument({
            id,
            documentName: documentToImport.name,
            path,
            companyId,
          });

          // posthog.capture('uploaded document', {
          //   source: 'documents page',
          // });

          if (response.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: `There was a problem uploading ${documentToImport.name}.`,
            });
          } else {
            success += 1;
          }
        }

        if (this.documentsToImport.length === success) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `You have successfully uploaded ${
              this.documentsToImport.length
            } document${this.documentsToImport.length > 1 ? 's' : ''}!`,
          });
        }
        if (!this.currentUser.is_advisor && !this.isFirmCommunicationDisabled) {
          let otherEntities = this.companies.some(
            c =>
              c.id != this.currentCompany.id &&
              this.currentCompany.owner_ids === c.owner_ids,
          );
          const response2 = await API.conversation.sendMessage({
            text: `uploaded ${success} document${
              success > 1 ? 's' : ''
            } through Documents (${this.currentCompany.tax_return_name +
              this.currentPath})`,
            conversationId: this.currentCompany.relevant_conversation_id,
            isLog: true,
          });
          if (response2.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'There was a problem.',
            });
            console.log(response2.data.error);
          }
        }

        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: has(e, 'message')
            ? get(e, 'message')
            : `There was a problem uploading the document${
                this.documentsToImport.length > 1 ? 's' : ''
              }.`,
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
        this.documentsToImport = [];
      }
    },
    pickDocuments() {
      const input = this.$refs.selectedDocuments;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocuments.click();
    },
    async addFile(e) {
      this.importDocumentsDialogOpened = true;
      this.documentImporting = true;
      let droppedFiles = e.dataTransfer.files;
      if (!droppedFiles) return;

      this.documentsToImport.push(...droppedFiles);
      this.documentImporting = false;
    },
    removeFile(file) {
      this.files = this.files.filter(f => {
        return f != file;
      });
    },
    async onDocumentsPicked(e) {
      this.documentImporting = true;
      const documents = e.target.files;

      if (documents) {
        this.documentsToImport.push(...documents);
        this.documentImporting = false;
      }
    },
    openDeleteConfirmationDialog(documentToDelete) {
      this.documentToDelete = documentToDelete;
      this.deleteConfirmationDialogOpened = true;
    },
    async downloadDocumentByLink(url, name) {
      const link = document.createElement('a');
      // If you don't know the name or want to use
      // the webserver default set name = ''
      link.setAttribute('download', name);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    endsWithAny(suffixes, string) {
      return suffixes.find(function(suffix) {
        return string.toLowerCase().endsWith(suffix);
      });
    },
    async renameItem() {
      try {
        this.loading = true;
        await API.integration.renameItem({
          itemName: this.selectedItem.name,
          newName:
            this.selectedItem.type === 'folder'
              ? this.newName
              : this.newName.concat('.', this.documentExtension),
          path: this.currentPath,
          companyId: this.currentCompanyId,
        });
        await this.getDocuments(this.currentPath);
        this.$store.dispatch('application/setSuccessNotification', {
          message: `You successfully renamed the ${this.selectedItem.type}!`,
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem renaming the document or folder.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
        if (!this.previewDialogOpened) {
          this.selectedItem = {
            name: '',
            type: 'file',
          };
        } else {
          this.selectedItem.name = this.newName.concat(
            '.',
            this.documentExtension,
          );
        }
        this.newName = '';
        this.renameFile = false;
        this.documentExtension = '';
      }
    },
    async previewDocument(document) {
      this.previewType = '';
      this.renameFile = false;
      this.previewLoaded = false;
      this.documentPreviewData = '';
      this.previewRotation = 0;
      this.selectedItem = document;
      this.previewType = this.endsWithAny(
        this.supportedExtensionsForPreview,
        document.link,
      );
      if (this.previewType) {
        if (this.previewType === 'pdf') {
          this.currentPage = 1;
          this.pdfLoaded = false;
        }
        this.previewDialogOpened = true;
      }
      try {
        const response = await API.integration.downloadDocument({
          companyId: this.currentCompanyId,
          link: document.link,
        });

        if (response.data.document) {
          this.documentPreviewData = response.data.document.data.link;
        } else {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem downloading the document.',
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        }
        if (isEmpty(this.previewType))
          this.downloadDocumentByLink(this.documentPreviewData, document.name);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.previewLoaded = true;
      }
    },
    async goToFolder(folderName) {
      if (!this.loading) {
        try {
          this.loading = true;
          this.currentPath = this.currentPath.concat('/', folderName);
          await this.getDocuments(this.currentPath);
          this.currentFolder = folderName;
        } catch (e) {
          console.log(e);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem accessing the folder',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    async goToPath(path) {
      if (!this.loading) {
        try {
          this.loading = true;
          this.currentPath = path;
          await this.getDocuments(this.currentPath);
          this.$router.push({
            query: { currentPath: this.currentPath || '/' },
          });
        } catch (e) {
          console.log(e);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem accessing the folder',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    async goBack() {
      if (this.currentFolder === this.$t('documents.allDocuments')) {
        return;
      }
      try {
        this.loading = true;
        let folderPath = this.currentPath.substring(
          0,
          this.currentPath.lastIndexOf('/'),
        );
        this.currentPath = folderPath;
        await this.getDocuments(this.currentPath);
        if (folderPath !== '') {
          this.currentFolder = this.currentPath.substring(
            this.currentPath.lastIndexOf('/') + 1,
            this.currentPath.length,
          );
        } else {
          this.currentFolder = this.$t('documents.allDocuments');
        }
      } finally {
        this.loading = false;
      }
    },
    async asyncForEach(array, callback) {
      for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
      }
    },
    async downloadDocument(doc) {
      try {
        const response1 = await API.integration.downloadDocument({
          companyId: this.currentCompanyId,
          link: doc.link,
        });
        this.downloadDocumentByLink(
          response1.data.document.data.link,
          doc.name,
        );
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: this.$t('documents.documentDownloadErrorMessage'),
        });
      }
    },
    async downloadFolder(folder) {
      try {
        await API.integration.downloadFolder({
          companyId: this.currentCompanyId,
          link: folder.link,
          name: folder.name,
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: this.$t('documents.folderDownloadErrorMessage'),
        });
      }
    },

    async downloadMultipleDocuments() {
      try {
        this.loading = true;
        this.downloadMultipleConfirmationDialogOpened = false;

        const zip = new JSZip();

        await Promise.all(
          this.selectedDocuments
            .filter(doc => doc.type === 'file')
            .map(doc => {
              return this.downloadDocumentAsBuffer(
                this.currentCompanyId,
                doc.link,
              ).then(buffer => {
                zip.file(doc.name, buffer);
              });
            }),
        );

        await zip.generateAsync({ type: 'blob' }).then(function(content) {
          download(content, 'files.zip', 'application/zip');
        });

        await this.getDocuments(this.currentPath);
        this.loading = false;
        this.selectedDocuments = [];
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: this.$t('documents.DocumentsBulkDownloadErrorMessage'),
        });
      }
    },

    async deleteMultipleDocuments() {
      try {
        this.loading = true;
        this.deleteMultipleConfirmationDialogOpened = false;

        await this.asyncForEach(this.selectedDocuments, async doc => {
          if (
            doc.name !== '_soraban' &&
            (!this.disableFolderAction || this.isAdvisor || doc.type === 'file')
          ) {
            const response1 = await API.integration.deleteDocument({
              documentName: doc.name,
              path: this.currentPath,
              companyId: this.currentCompanyId,
              isFolder: doc.type === 'folder',
            });
          }
        });
        this.$store.dispatch('application/setSuccessNotification', {
          message: this.$t('documents.documentDeletedSuccessMessage'),
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: this.$t('documents.documentDeletedSuccessMessage'),
          errorToSend: response.data.error,
          userEmail: this.currentUser.email,
        });
      } finally {
        this.loading = false;
      }
    },
    async deleteDocument(document) {
      try {
        this.loading = true;
        this.deleteConfirmationDialogOpened = false;
        const response = await API.integration.deleteDocument({
          documentName: document.name,
          path: this.currentPath,
          companyId: this.currentCompanyId,
          isFolder: document.type === 'folder',
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    async getDocuments(path) {
      try {
        const response = await API.integration.getDocuments({
          companyId: this.currentCompanyId,
          path,
        });
        if (response.data.documents) {
          this.documents = response.data.documents.map(d => ({
            ...d,
            renameFile: false,
          }));
          this.selectedDocuments = [];
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: "The path doesn't exist",
          });
          this.currentPath = '';
          const response2 = await API.integration.getDocuments({
            companyId: this.currentCompanyId,
          });

          this.documents = response2.data.documents.map(d => ({
            ...d,
            renameFile: false,
          }));
        }
      } catch (e) {
        console.log(e);
      }
    },
    async createFolder() {
      try {
        this.loading = true;
        this.createFolderDialogOpened = false;
        const response = await API.integration.createFolder({
          path: this.currentPath.concat('/', this.newFolderName),
          companyId: this.currentCompanyId,
        });
        await this.getDocuments(this.currentPath);
      } catch (e) {
        console.log(e);
      } finally {
        this.newFolderName = '';
        this.loading = false;
      }
    },
    async getExistingQuestionnaires() {
      const response = await API.questionnaire.getExistingQuestionnaires(
        this.currentCompanyId,
      );
      this.existingQuestionnaires = response.data;
      console.log(this.existingQuestionnaires);
    },
    goToQuestionnaire(id) {
      const hash =
        this.currentCompany.name
          .replaceAll(' ', '-')
          .replaceAll(',', '')
          .replaceAll('#', '') +
        '-' +
        this.currentCompany.hash_id;
      this.$router.push(`/questionnaire/${hash}/?questionnaireId=${id}`);
    },
  },
  async created() {
    this.loading = true;

    if (this.isFirmDocumentsDisabled) {
      this.$store.dispatch('application/setErrorNotification', {
        message: 'You do not have access to this page.',
      });
      this.$router.go(-1);
    }

    try {
      this.currentPath =
        this.$route.query.path === 'all' ||
        !this.$route.query.path ||
        this.$route.query.path === '/'
          ? ''
          : this.$route.query.path;

      await this.getDocuments(this.currentPath);

      if (this.currentPath !== '') {
        this.currentFolder = this.currentPath.substring(
          this.currentPath.lastIndexOf('/') + 1,
          this.currentPath.length,
        );
      } else {
        this.currentFolder = this.$t('documents.allDocuments');
      }

      if (this.$route.query.previewName) {
        const previewDoc = this.documents.find(doc => {
          return doc.name === this.$route.query.previewName;
        });

        if (previewDoc) {
          await this.previewDocument(previewDoc);
        }
      }
      this.getExistingQuestionnaires();
    } finally {
      this.loading = false;
    }
  },

  watch: {
    $route: {
      async handler(route) {
        if (this.$route.query.path) {
          try {
            this.loading = true;
            this.currentPath =
              route.query.path === 'all' ? '' : this.$route.query.path;

            const response1 = await API.integration.getDocuments({
              companyId,
            });

            if (response1.data.documents) {
              this.documents = response1.data.documents;
              if (this.currentPath !== '') {
                this.currentFolder = this.currentPath.substring(
                  this.currentPath.lastIndexOf('/') + 1,
                  this.currentPath.length,
                );
              } else {
                this.currentFolder = this.$t('documents.allDocuments');
              }
            } else {
              const response2 = await API.integration.getDocuments({
                companyId,
              });
              this.documents = response2.data.documents;
              this.currentFolder = this.$t('documents.allDocuments');
            }
          } finally {
            this.loading = false;
          }
        }
      },
    },
  },
};
</script>

<style scoped lang="postcss">
.mobile-dialog-footer {
  width: 100%;
  position: fixed;
  bottom: 0;
}
.documents-container {
  border: 1px solid #bbb;
  border-radius: 4px;
  background-color: white;
  overflow: hidden;
}

.folder-item:first-child {
  border-top: none;
}
.folder-item {
  padding: 12px;
  border-top: 1px solid #bbb;
  cursor: pointer;

  &:hover {
    background-color: #f1f1f1;
  }
}

.documents {
  &__icon {
    font-size: 18px;
  }
}
.document-action {
  &__icon {
    font-size: 20px;
  }
}
.documents-row {
  &__updated-date {
    font-weight: 200;
    color: rgba(9, 30, 66, 0.6);
  }
}
.image-holder {
  display: block;
  width: 200px;
  height: 200px;
  cursor: pointer;
  background-size: cover;
  background-position: center center;
}
</style>
